import { Twitter, Linkedin, Instagram, Mail, ShieldCheck } from "lucide-react";
import { Link } from "react-router-dom";
import { resetConsent, isAnalyticsEnabled } from "@/lib/analytics";

export default function Footer() {
  return (
    <footer className="relative border-t border-white/5 bg-[#0A0A0A] pt-20 pb-10">
      <div className="absolute inset-x-0 top-0 h-px gold-line" />
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-10">
          <div className="col-span-2">
            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-[#F3CE5A] to-[#8B6914] flex items-center justify-center shadow-[0_0_24px_rgba(212,175,55,0.35)]">
                <span className="font-display text-black text-base font-bold">F</span>
              </div>
              <span className="font-display text-2xl text-white tracking-tight">Facetra</span>
            </div>
            <p className="mt-5 text-white/50 text-sm max-w-sm leading-relaxed">
              Premium AI software for jewellery ateliers. Built in India, for the craft that taught the
              world how to make jewellery.
            </p>
            <div className="mt-6 flex items-center gap-3">
              {[
                { Icon: Twitter, href: "#" },
                { Icon: Linkedin, href: "#" },
                { Icon: Instagram, href: "#" },
                { Icon: Mail, href: "mailto:hello@facetra.studio" },
              ].map(({ Icon, href }, i) => (
                <a
                  key={i}
                  href={href}
                  data-testid={`footer-social-${i}`}
                  className="w-9 h-9 rounded-full border border-white/10 flex items-center justify-center text-white/55 hover:text-[#D4AF37] hover:border-[#D4AF37]/45 transition-all"
                >
                  <Icon className="w-4 h-4" strokeWidth={1.5} />
                </a>
              ))}
            </div>

            {/* Trust badge — Razorpay live merchant */}
            <div
              data-testid="footer-razorpay-badge"
              className="mt-6 inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-white/8 bg-[#0E0E0E] text-xs text-white/65"
            >
              <ShieldCheck className="w-3.5 h-3.5 text-[#D4AF37]" />
              Secure payments by <span className="text-white/85 font-medium">Razorpay</span>
            </div>
          </div>

          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-white/40 mb-4">Product</div>
            <ul className="space-y-2.5 text-sm text-white/65">
              <li><a href="/#features" className="hover:text-[#D4AF37] transition">Features</a></li>
              <li><a href="/#intelligence" className="hover:text-[#D4AF37] transition">Intelligence</a></li>
              <li><a href="/#showcase" className="hover:text-[#D4AF37] transition">Showcase</a></li>
              <li><a href="/#pricing" className="hover:text-[#D4AF37] transition">Pricing</a></li>
            </ul>
          </div>

          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-white/40 mb-4">Company</div>
            <ul className="space-y-2.5 text-sm text-white/65">
              <li><Link to="/about" data-testid="footer-link-about" className="hover:text-[#D4AF37] transition">About</Link></li>
              <li><Link to="/how-it-works" data-testid="footer-link-how-it-works" className="hover:text-[#D4AF37] transition">How it works</Link></li>
              <li><Link to="/for-jewellers" data-testid="footer-link-for-jewellers" className="hover:text-[#D4AF37] transition">For ateliers</Link></li>
              <li><Link to="/for-designers" data-testid="footer-link-for-designers" className="hover:text-[#D4AF37] transition">For designers</Link></li>
              <li><Link to="/for-students" data-testid="footer-link-for-students" className="hover:text-[#D4AF37] transition">For students</Link></li>
              <li><Link to="/contact" data-testid="footer-link-contact" className="hover:text-[#D4AF37] transition">Contact</Link></li>
            </ul>
          </div>

          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-white/40 mb-4">Library</div>
            <ul className="space-y-2.5 text-sm text-white/65">
              <li><Link to="/articles" data-testid="footer-link-articles" className="hover:text-[#D4AF37] transition">Articles</Link></li>
              <li><Link to="/glossary" data-testid="footer-link-glossary" className="hover:text-[#D4AF37] transition">Glossary</Link></li>
              <li>
                <Link
                  to="/signup"
                  data-testid="footer-cta-signup"
                  className="inline-flex items-center gap-1.5 mt-1 text-[#D4AF37] hover:text-[#F3CE5A] transition"
                >
                  Create account →
                </Link>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-white/5 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-xs text-white/35">
            © {new Date().getFullYear()} Facetra Design Studio Private Limited — Crafted with care in India.
          </p>
          <div className="flex items-center gap-5 text-xs text-white/40 flex-wrap justify-center">
            <Link to="/privacy" data-testid="footer-link-privacy" className="hover:text-white/85 transition">Privacy</Link>
            <Link to="/terms" data-testid="footer-link-terms" className="hover:text-white/85 transition">Terms</Link>
            <Link to="/refund" data-testid="footer-link-refund" className="hover:text-white/85 transition">Refund Policy</Link>
            <Link to="/contact" data-testid="footer-link-contact-2" className="hover:text-white/85 transition">Contact</Link>
            {isAnalyticsEnabled() && (
              <button
                type="button"
                onClick={resetConsent}
                data-testid="footer-link-cookie-preferences"
                className="hover:text-white/85 transition cursor-pointer"
              >
                Cookie preferences
              </button>
            )}
          </div>
        </div>
      </div>
    </footer>
  );
}
